import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUsers,
    faDollarSign,
    faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";

import { useAuth } from "../../../../firebase/AuthProvider";
import { OwnerComplianceCombinedCurrentResponseDto } from "../../../../interfaces";
import { GetCombinedComplianceCurrentAsync } from "../../../../services/OwnerMetricsService/OwnerMetricsService";
import { OwnerGraphTrend } from "../../../../Data/SD";
import { ownerTrendSubjet$, selectedRegionIdFromHeader$, selectWaiverTypeIdFromHeader$, UpdateOnwerTrendSubject } from "../../../../services/Subjects/userSubject";

const OverviewHeader: React.FC = () => {
    const { currentUser } = useAuth();
    const [currentGraphTrend, setCurrentGraphTrend] = useState<OwnerGraphTrend>();
    const [selectedRegionId, setSelectedRegionId] = useState<number | null>();
    const [selectedWaiverId, setSelectedWaiverId] = useState<number | null>();

    const [compliance, setCompliance] = useState<OwnerComplianceCombinedCurrentResponseDto>();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const trendSub = ownerTrendSubjet$.subscribe({
            next: (trend) => setCurrentGraphTrend(trend),
        });

        const regionSub = selectedRegionIdFromHeader$.subscribe({
            next: (regionId) => setSelectedRegionId(regionId),
        });

        const waiverSub = selectWaiverTypeIdFromHeader$.subscribe({
            next: (waiverId) => setSelectedWaiverId(waiverId),
        });

        return () => {
            trendSub.unsubscribe();
            regionSub.unsubscribe();
            waiverSub.unsubscribe();
        };
    }, []);

    useEffect(() => {
        GetCompliance();
    }, [selectedRegionId, selectedWaiverId, currentUser]);

    const GetCompliance = async () => {
        setLoading(true);
        const token = await currentUser?.getIdToken();
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const result = await GetCombinedComplianceCurrentAsync(token, selectedRegionId, selectedWaiverId);
            if (result.success) {
                setCompliance(result.data);
            } else {
                toast.error(result.message);
            }
        } catch (error: any) {
            const msg = error instanceof AxiosError ? error.response?.data.message : error.message || "Error to load the Data";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeTreanSubject = (trend: OwnerGraphTrend) => {
        UpdateOnwerTrendSubject(trend);
    };

    /**
     * Renders a trend value with dynamic coloring and a default fallback.
     * @param value - The numerical change value (can be null/undefined).
     * @param suffix - Optional suffix (e.g., "%").
     */
    const renderTrend = (value?: number, suffix: string = "") => {
        // Default state for new companies or missing data
        if (value === undefined || value === null) {
            return <span className="text-sm font-semibold text-gray-400">0{suffix}</span>;
        }

        const isPositive = value > 0;
        const isNegative = value < 0;

        // Determine color: Green for positive, Red for negative, Gray for zero
        const colorClass = isPositive
            ? "text-green-600"
            : isNegative
                ? "text-red-500"
                : "text-gray-500";

        // Add explicit '+' for positive numbers
        const displayValue = isPositive ? `+${value}` : `${value}`;

        return (
            <span className={`text-sm font-semibold ${colorClass}`}>
                {displayValue}{suffix}
            </span>
        );
    };

    type FormatStyle = 'commas' | 'truncate';

    const formatCurrency = (value: number, style: FormatStyle = 'commas'): string => {
        if (style === 'truncate') {
            if (value >= 1_000_000) {
                return `$${(value / 1_000_000).toFixed(2)}M`;
            } else if (value >= 1_000) {
                return `$${(value / 1_000).toFixed(2)}k`;
            }
        }

        // Default: Adds commas and ensures two decimal places
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value);
    };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* PARTICIPANTS */}
            <div
                onClick={() => handleChangeTreanSubject(OwnerGraphTrend.Participants)}
                className={`cursor-pointer rounded-xl bg-white p-3 transition ${currentGraphTrend === OwnerGraphTrend.Participants ? "border border-gray-200" : "border-none"}`}
            >
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold tracking-wide text-gray-500">
                        PARTICIPANTS
                    </span>
                    <FontAwesomeIcon icon={faUsers} className="text-gray-400" />
                </div>

                <div className="mt-3 text-2xl font-bold text-gray-900 flex items-center gap-2">
                    {loading ? (
                        <Spinner size="sm" />
                    ) : (
                        <>
                            {compliance?.participants?.total}
                            {/* Uses 'change' field with % suffix */}
                            {renderTrend(compliance?.participants?.change, "%")}
                        </>
                    )}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-gray-500">
                    <div>
                        Linkages <strong className="block text-gray-900">{compliance?.participants?.linkages}</strong>
                    </div>
                    <div>
                        Closures <strong className="block text-gray-900">{compliance?.participants?.closures}</strong>
                    </div>
                    <div>
                        Certified <strong className="block text-gray-900">{compliance?.participants?.certified}</strong>
                    </div>
                </div>
            </div>

            {/* RECURRING BILLABLE */}
            <div
                onClick={() => handleChangeTreanSubject(OwnerGraphTrend.Billing)}
                className={`cursor-pointer rounded-xl bg-white p-3 transition ${currentGraphTrend === OwnerGraphTrend.Billing ? "border border-gray-200" : "border-none"}`}
            >
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold tracking-wide text-gray-500">
                        RECURRING BILLABLE
                    </span>
                    <FontAwesomeIcon icon={faDollarSign} className="text-gray-400" />
                </div>

                <div className="mt-3 text-2xl font-bold text-gray-900 flex items-center gap-2">
                    {loading ? (
                        <Spinner size="sm" />
                    ) : (
                        <>
                            {/* {formatCurrency(57438.00, 'truncate')} */}
                            {formatCurrency(compliance?.billing?.billableAmount || 0, 'truncate')}
                            {renderTrend(compliance?.billing?.change, "%")}

                        </>
                    )}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-gray-500">
                    <div>
                        Eligible <strong className="block text-gray-900">{compliance?.billing?.billableParticipants}</strong>
                    </div>
                    <div>
                        Not Cert. <strong className="block text-gray-900">{compliance?.billing?.notCertified}</strong>
                    </div>
                    <div>
                        Change <strong className="block text-gray-900">{compliance?.billing?.change}</strong>
                    </div>
                </div>
            </div>

            {/* TASK COMPLIANCE */}
            <div
                onClick={() => handleChangeTreanSubject(OwnerGraphTrend.Task)}
                className={`cursor-pointer rounded-xl bg-white p-3 transition ${currentGraphTrend === OwnerGraphTrend.Task ? "border border-gray-200" : "border-none"}`}
            >
                <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold tracking-wide text-gray-500">
                        TASK COMPLIANCE
                    </span>
                    <FontAwesomeIcon icon={faCheckCircle} className="text-gray-400" />
                </div>

                <div className="mt-3 text-2xl font-bold text-gray-900 flex items-center gap-2">
                    {loading ? (
                        <Spinner size="sm" />
                    ) : (
                        <>
                            {compliance?.tasks?.compliancePercent}%
                            {/* Uses 'complianceChange' field with % suffix */}
                            {renderTrend(compliance?.tasks?.complianceChange, "%")}
                        </>
                    )}
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-xs text-gray-500">
                    <div>
                        Late <strong className="block text-gray-900">{compliance?.tasks?.late}</strong>
                    </div>
                    <div>
                        Rejected <strong className="block text-gray-900">{compliance?.tasks?.rejected}</strong>
                    </div>
                    <div>
                        Extended <strong className="block text-gray-900">{compliance?.tasks?.extended}</strong>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default OverviewHeader;